import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star } from 'lucide-react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import SuccessCelebration from './SuccessCelebration';
import FailureFeedback from './FailureFeedback';
import styles from './LessonContentRenderer.module.css';

interface InlineQuizProps {
    question: string;
    options: string; // Semicolon separated
    correctIndex: string;
}

const InlineQuiz: React.FC<InlineQuizProps> = ({ question, options, correctIndex }) => {
    const [selected, setSelected] = useState<number | null>(null);
    const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
    const [showCelebration, setShowCelebration] = useState(false);
    const [showFailure, setShowFailure] = useState(false);
    const [isClient, setIsClient] = useState(false);

    // InlineQuiz is now exclusively for "pop-quiz" (gold standard).
    // We strip the "Pop Quiz:" prefix if it still exists in the content.
    const cleanQuestion = (question || '').replace(/^pop\s*quiz:\s*/i, '').trim();

    // Ensure portals only render on client side
    useEffect(() => {
        setIsClient(true);
    }, []);

    let optionList: string[] = [];
    try {
        // Check if options looks like a JSON array
        if (options.trim().startsWith('[')) {
            optionList = JSON.parse(options);
        } else {
            // Legacy semicolon separated format
            optionList = options.split(';').map(o => o.trim());
        }
    } catch (e) {
        console.warn('InlineQuiz: Failed to parse options, falling back to split', options);
        optionList = options.split(';').map(o => o.trim());
    }

    // Determine the correct index (handle both index and string match)
    let correctIdx = -1;
    const parsedIndex = parseInt(correctIndex, 10);

    if (!isNaN(parsedIndex)) {
        correctIdx = parsedIndex - 1;
    } else {
        // Find by string match
        correctIdx = optionList.findIndex(opt => opt.trim() === correctIndex.trim());
    }


    const handleSelect = (index: number) => {
        if (selected !== null) return;
        setSelected(index);
        const correct = index === correctIdx;
        setIsCorrect(correct);

        if (correct) {
            setTimeout(() => setShowCelebration(true), 600);
        } else {
            setTimeout(() => setShowFailure(true), 600);
        }
    };

    const handleReset = () => {
        setShowFailure(false);
        // Reset state for retry
        setSelected(null);
        setIsCorrect(null);
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className={`${styles.inlineQuizContainer} ${styles.inlineQuizGold}`}
        >
            {isClient && showCelebration && createPortal(
                <SuccessCelebration show={showCelebration} onComplete={() => setShowCelebration(false)} />,
                document.body
            )}
            {isClient && showFailure && createPortal(
                <FailureFeedback show={showFailure} onComplete={handleReset} />,
                document.body
            )}

            <div className={styles.inlineQuizGoldHeader}>
                <div>
                    <div className={styles.inlineQuizGoldBadge}>
                        <Trophy size={14} />
                        <span>Pop Quiz Challenge</span>
                    </div>
                    <h4 className={styles.inlineQuizQuestion}>{cleanQuestion}</h4>
                </div>
                <div className={styles.inlineQuizGoldLottie}>
                    <DotLottieReact
                        src="/assets/animations/quiz-gold-trophy.lottie"
                        autoplay
                        loop
                    />
                </div>
            </div>

            <div className={styles.inlineQuizOptions}>
                {optionList.map((option, idx) => {
                    let stateClass = '';
                    if (selected === idx) {
                        stateClass = isCorrect ? styles.optionCorrect : styles.optionIncorrect;
                    }

                    return (
                        <button
                            key={idx}
                            onClick={() => handleSelect(idx)}
                            disabled={selected !== null}
                            className={`${styles.inlineQuizOption} ${stateClass}`}
                        >
                            <span className={styles.optionLabel}>{String.fromCharCode(65 + idx)}</span>
                            <span className={styles.optionText}>{option}</span>
                        </button>
                    );
                })}
            </div>

            <AnimatePresence>
                {selected !== null && isCorrect && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className={styles.inlineQuizFeedback}
                    >
                        <p className={styles.feedbackSuccess}>✨ Correct! You've mastered this concept.</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default InlineQuiz;

